// Plan gratuit & freemium — utilitaires partagés client/serveur.
//
// Le plan gratuit donne 100 crédits + UN seul module au choix parmi
// {transcription, codification, rapport}. Dès qu'un module est utilisé,
// les autres sont verrouillés via `profiles.free_module_used`.

import { FREE_PLAN } from './credit-costs'

/** Type stocké côté DB. null = aucun module utilisé. */
export type FreeModuleUsed = 'transcription' | 'codification' | 'rapport' | null

export interface UsageLimits {
  transcription_count: number
  codification_count: number
  note_count: number
  correction_count: number
  rapport_count: number
  is_pro: boolean
  /** Solde de crédits (plan gratuit comme Pro). */
  credits: number
  /** Module gratuit déjà utilisé — verrouille les autres. */
  free_module_used: FreeModuleUsed
}

export type LimitKey =
  | 'transcription_count'
  | 'codification_count'
  | 'note_count'
  | 'correction_count'
  | 'rapport_count'

/**
 * @deprecated Plafonds historiques (1 par module). Plus utilisés pour bloquer :
 * le plan gratuit est désormais piloté par les crédits et `free_module_used`.
 * Conservés pour les imports legacy ; toute nouvelle logique doit utiliser
 * `getFreePlanModuleStatus` ci-dessous.
 */
export const FREE_LIMITS: Record<LimitKey, number> = {
  transcription_count: 1,
  codification_count: 5,
  note_count: 1,
  correction_count: 1,
  rapport_count: 1,
}

const LS_KEY = 'qualyse_free_limits'

export const DEFAULT_LIMITS: UsageLimits = {
  transcription_count: 0,
  codification_count: 0,
  note_count: 0,
  correction_count: 0,
  rapport_count: 0,
  is_pro: false,
  credits: 0,
  free_module_used: null,
}

export function getLocalLimits(): UsageLimits {
  if (typeof window === 'undefined') return { ...DEFAULT_LIMITS }
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return { ...DEFAULT_LIMITS }
    return { ...DEFAULT_LIMITS, ...JSON.parse(raw) }
  } catch {
    return { ...DEFAULT_LIMITS }
  }
}

export function setLocalLimits(limits: UsageLimits): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(LS_KEY, JSON.stringify(limits))
}

export function incrementLocalLimit(key: LimitKey): UsageLimits {
  const limits = getLocalLimits()
  const updated = { ...limits, [key]: (limits[key] as number) + 1 }
  setLocalLimits(updated)
  return updated
}

/** @deprecated Voir `getFreePlanModuleStatus` — plus représentatif du modèle actuel. */
export function isLimitReached(limits: UsageLimits, key: LimitKey, count?: number): boolean {
  if (limits.is_pro) return false
  const current = count !== undefined ? count : (limits[key] as number)
  return current >= FREE_LIMITS[key]
}

// ── Statut de blocage des modules en plan gratuit ───────────────────────────

/**
 * Identifiant de verrou correspondant à un module côté `free_module_used`.
 * Un module bloqué côté UI = un module verrouillé par un autre déjà utilisé.
 */
export type FreeModuleKind = 'transcription' | 'codification' | 'rapport' | 'note-tech' | 'correction'

export interface FreePlanModuleStatus {
  /** Bloqué pour le plan gratuit (impossible de lancer, doit passer Pro). */
  blocked: boolean
  /** true → autre module gratuit déjà utilisé. */
  lockedByOther: boolean
  /** true → module réservé Pro indépendamment du verrou. */
  proOnly: boolean
  /** true → solde insuffisant pour démarrer une session. */
  insufficientCredits: boolean
  /** Crédits manquants (uniquement pour coding/analysis, forfaits fixes). */
  costNeeded?: number
  /** Minutes encore transcriptibles avec le solde actuel (plan gratuit). */
  minutesRemaining?: number
}

/**
 * Détermine l'état d'un module pour l'utilisateur courant. Côté UI uniquement
 * (les contrôles serveur restent dans `checkCredits`). En plan Pro, jamais bloqué
 * par cette fonction (le contrôle se fait sur le solde dans le bouton lancement).
 */
export function getFreePlanModuleStatus(limits: UsageLimits, kind: FreeModuleKind): FreePlanModuleStatus {
  if (limits.is_pro) {
    return { blocked: false, lockedByOther: false, proOnly: false, insufficientCredits: false }
  }
  // Modules réservés au Pro. `rapport` les a rejoints le 2026-10-12 : la liste
  // qui fait autorité est `PRO_ONLY_MODULES` (credit-costs.ts), côté serveur.
  if (kind === 'note-tech' || kind === 'correction' || kind === 'rapport') {
    return { blocked: true, lockedByOther: false, proOnly: true, insufficientCredits: false }
  }
  const used = limits.free_module_used
  const matches = used === kind
  const lockedByOther = !!used && !matches
  if (lockedByOther) {
    return { blocked: true, lockedByOther: true, proOnly: false, insufficientCredits: false }
  }
  // Solde — codif et rapport sont des forfaits fixes.
  if (kind === 'codification') {
    const need = FREE_PLAN.codingFlat
    const lack = limits.credits < need
    return { blocked: lack, lockedByOther: false, proOnly: false, insufficientCredits: lack, costNeeded: need }
  }
  // La branche `kind === 'rapport'` qui calculait un forfait gratuit de
  // `FREE_PLAN.analysisFlat` a été RETIRÉE : elle est devenue inatteignable
  // avec le passage du rapport en Pro-only, traité plus haut. La laisser aurait
  // laissé croire qu'un plan gratuit peut encore produire un rapport.
  // Transcription — coût par minute, on expose les minutes restantes.
  const minutesRemaining = Math.max(0, limits.credits / FREE_PLAN.transcriptionPerMinute)
  const exhausted = limits.credits <= 0
  return {
    blocked: exhausted,
    lockedByOther: false,
    proOnly: false,
    insufficientCredits: exhausted,
    minutesRemaining,
  }
}
